// Pure state derivation for the MyAccount WhatsApp self-start tab. Sibling of
// store/waOnboarding.js (operator panel). Mirror-tested in
// src/store/__tests__/whatsapp-selfstart.test.js — KEEP IN SYNC.

// The step vocabulary has ONE owner: waOnboarding.js STEPS, which itself mirrors the
// backend (conversations-service/lib/waOnboarding/state.js). Re-listing it here would
// give the customer tab a second copy free to drift from the operator panel.
import { STEPS as ORDER } from './waOnboarding';

// Derived positionally, not re-typed: everything from "customer finished Meta signup"
// up to (not including) live. A step inserted into STEPS inside that band is picked up
// automatically instead of silently falling through to not_eligible.
const IN_PROGRESS_STEPS = ORDER.slice(ORDER.indexOf('waba_connected'), ORDER.indexOf('live'));

// link_sent deliberately maps to not_started: my-connect-init advances to link_sent on
// every click, so an abandoned FB popup must leave the Connect button clickable.
export function deriveTabState({ eligible, enabled, step, disabled }) {
  // `enabled` is the authoritative flag on the backend and stays authoritative here: if the
  // channel is actually ON, a leftover disabled marker must never hide the live card.
  if (enabled) return 'live';
  // Checked BEFORE the step branches. A site switched off ON PURPOSE has usually made real
  // progress — often all the way to step 'live' — so a step-first ordering would render it as
  // 'live' or 'in_progress', either claiming WhatsApp is active for a practice whose WhatsApp
  // is off, or promising a setup nobody is working on any more.
  if (disabled) return 'disabled';
  if (step === 'live') return 'live';
  if (IN_PROGRESS_STEPS.includes(step)) return 'in_progress';
  if (!eligible) return 'not_eligible';
  return 'not_started';
}

const MILESTONES = [
  { key: 'connected', label: 'WhatsApp account connected' },
  { key: 'number',    label: "We're registering your number" },
  { key: 'template',  label: 'Reminder template pending WhatsApp approval' },
  { key: 'ready',     label: 'Ready — WhatsApp is live' },
];
const REACHED_AT = { connected: 'waba_connected', number: 'sender_registered', template: 'template_submitted', ready: 'live' };

/** [{ key, label, state: done|current|pending }] for the customer stepper. */
export function milestoneStates(step, enabled) {
  const idx = (s) => ORDER.indexOf(s);
  const effective = enabled ? 'live' : step;
  let currentSeen = false;
  return MILESTONES.map((m) => {
    const done = idx(effective) >= idx(REACHED_AT[m.key]);
    if (done) return { ...m, state: 'done' };
    if (!currentSeen) { currentSeen = true; return { ...m, state: 'current' }; }
    return { ...m, state: 'pending' };
  });
}

/** hasIssue collapses ANY sender problem to one neutral line — raw errors are operator-only. */
export function statusLine(step, hasIssue) {
  if (hasIssue) return "We hit a snag finishing your setup — our team is on it.";
  if (step === 'waba_connected') return 'Connected — Teamio is completing your setup.';
  return '';
}

/* ══ SELF-SERVE WIZARD (2026-09-02) ═══════════════════════════════════════════════════════════════
 * Everything below drives the full self-serve wizard, which renders INSTEAD of the four cards above
 * when `my-status` says `wizard.enabled` (decision A3 — super-admins only during the driven phase).
 * `deriveTabState` and `milestoneStates` are deliberately untouched: a practice without the wizard
 * must see exactly the tab it saw yesterday, and the wizard's two new PRE-connect backend steps
 * ('triage', 'number_pending') fall outside the positional IN_PROGRESS_STEPS slice by design.
 * ════════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Is this practice on the one branch the wizard automates — buy a US number?
 *
 * SOURCE: conversations-service/lib/waOnboarding/triage.js `isUsPurchasePath`, read 2026-09-11.
 * Mirrored rather than derived from the response because the backend does not send the branch, it
 * sends the ANSWERS; the screen the practice sees and the endpoint that would accept their next
 * click must agree about which branch they are on, or the wizard offers a purchase the server
 * refuses with `not_purchase_path`.
 *
 * Kept equivalent to the backend predicate, including the array guard: `typeof [] === 'object'`, so
 * a bare typeof check would let an array through, match none of the negative branches and return
 * TRUE — offering a purchase screen on no basis at all. And like the backend, BYO is peeled off by
 * CALLING `requiresByoAck` rather than re-typing its test here: a second copy of "is this BYO?" in
 * this file is how the keep-it-on-the-app answer could reach one and not the other.
 */
export function isUsPurchasePath(triage) {
  if (!triage || typeof triage !== 'object' || Array.isArray(triage)) return false;
  if (requiresByoAck(triage)) return false;
  if (triage.wantsLocalNumber === true) return false;
  return true;
}

/**
 * Does this triage answer set put the practice on the bring-your-own-number path — the ONE path that
 * moves a number the practice ALREADY uses, and so the one that must acknowledge the consequences of
 * moving it (the WhatsApp app going dark on that number, lost history, the reply-window rule)?
 *
 * SOURCE: conversations-service/lib/waOnboarding/triage.js `isByoPath`, read 2026-09-11. Kept as the
 * POSITIVE two-field test (`hasWhatsAppNumber && numberOnApp`), NOT `!isUsPurchasePath`: that negation
 * is ALSO true for a practice wanting a brand-new local NON-US number, which has no existing number to
 * move, nothing to lose and nothing to acknowledge — folding it in would demand acknowledgements it
 * cannot possibly apply to and block a legitimate path. The array guard is here for the same
 * fail-closed reason `isUsPurchasePath` carries it (`typeof [] === 'object'`).
 *
 * KEEPING the number on the app is not BYO (2026-09-11): a practice whose number is live on the
 * WhatsApp app can keep it there and give Teamio a new number — nothing moves, nothing is lost. Only a
 * strict `keepsNumberOnApp === true` means keep. Absent/null is a move (every answer set and every
 * build from before this answer existed meant "move"), and a truthy non-boolean is a move too — the
 * path that spends nothing is the safe landing for noise, never the purchase.
 */
export function requiresByoAck(answers) {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return false;
  return answers.hasWhatsAppNumber === true && answers.numberOnApp === true
    && answers.keepsNumberOnApp !== true;
}

/**
 * Are ALL of the server-declared BYO acknowledgement boxes ticked?
 *
 * The keys come from `my-status`'s `byoAckWording.items` — the wizard NEVER hardcodes them — so this
 * takes them as an argument rather than knowing them. FAIL-CLOSED on an empty key set: if the wording
 * never loaded there is nothing to tick, and a screen that let the practice continue without
 * acknowledging anything is exactly the inert-config trap the backend's `byo_ack_required` gate
 * exists to close. Returning false keeps Continue disabled so the screen shows its "could not load"
 * state instead of waving the practice through to a save the server would 400.
 */
export function isByoAckComplete(answers, ackKeys) {
  const keys = Array.isArray(ackKeys) ? ackKeys.filter((k) => typeof k === 'string' && k !== '') : [];
  if (keys.length === 0) return false;
  const d = (answers && typeof answers === 'object' && !Array.isArray(answers)) ? answers : {};
  const acks = (d.acknowledgements && typeof d.acknowledgements === 'object' && !Array.isArray(d.acknowledgements))
    ? d.acknowledgements
    : {};
  return keys.every((k) => acks[k] === true);
}

/**
 * Meta's display-name bounds, enforced where the practice types the name rather than a week later
 * when a review cycle has already been spent.
 * SOURCE: conversations-service/lib/waOnboarding/triage.js `DISPLAY_NAME_MIN` / `DISPLAY_NAME_MAX`,
 * read 2026-09-02.
 */
export const DISPLAY_NAME_MIN = 3;
export const DISPLAY_NAME_MAX = 512;

/**
 * The accepted answers to "who looks after your Facebook account?".
 * SOURCE: conversations-service/lib/waOnboarding/triage.js — `facebookAccess` is stored as a
 * trimmed string; these are the three the wizard offers (design section 1, step 2), read 2026-09-02.
 */
export const FACEBOOK_ACCESS_ANSWERS = ['self', 'other', 'none'];

/**
 * Are the practice's answers on this screen complete enough to submit?
 *
 * Only the three screens BEFORE the single my-triage POST collect answers, so only they can be
 * "valid". Every other screen returns false because it has nothing to submit — their own buttons
 * (Connect, Refresh) are gated on something else entirely (the Meta SDK being loaded, a request
 * being in flight), which is the shell's business, not this function's.
 *
 * This is the inert-config rule applied at the earliest possible point: a config that could never
 * carry the setup forward must be REFUSED, not stored-and-reported-successful. The backend refuses
 * the same three cases (`validateTriageAnswers`); this stops the practice from getting that far and
 * — the part the backend cannot do — keeps Continue visibly disabled so they can see WHY.
 */
export function isWizardStepValid(stepKey, draft) {
  const d = (draft && typeof draft === 'object' && !Array.isArray(draft)) ? draft : {};
  if (stepKey === 'intro') return true;

  if (stepKey === 'triage') {
    // Unanswered is `null`/undefined, and it is NOT the same as `false` — "no, I don't have a
    // number" is a real answer that routes to a purchase.
    if (d.hasWhatsAppNumber !== true && d.hasWhatsAppNumber !== false) return false;
    // Having a number without saying whether it is live in the WhatsApp app leaves the branch
    // undecidable: bring-your-own (a person) and buy-a-number (a purchase) are opposite outcomes.
    if (d.hasWhatsAppNumber === true && d.numberOnApp !== true && d.numberOnApp !== false) return false;
    // STRICTER THAN THE BACKEND, on purpose, and in the safe direction (we may refuse what the
    // server would accept, never the reverse). Triage is submitted ONCE, and the branch that needs
    // this number is a human handoff — a Slack page that says "they have a number" without saying
    // which one is a handoff nobody can act on, and by then the answers are frozen.
    if (d.hasWhatsAppNumber === true && String(d.byoNumber || '').trim() === '') return false;
    return FACEBOOK_ACCESS_ANSWERS.includes(d.facebookAccess);
  }

  if (stepKey === 'confirm') {
    const name = typeof d.displayName === 'string' ? d.displayName.trim() : '';
    if (name.length < DISPLAY_NAME_MIN || name.length > DISPLAY_NAME_MAX) return false;
    // A stored consent that was never actually given is worse than no record at all: it is the
    // artifact we would point at if WhatsApp ever asked how these patients opted in.
    return d.consentAcknowledged === true;
  }

  return false;
}

/**
 * Which wizard SCREEN to render for a my-status payload. The wizard's single source of truth.
 *
 * The backend's steps are progress; these are screens, and the two vocabularies are not the same
 * size (see whatsapp-wizard/wizardSteps.js). One backend step maps to several screens — every
 * screen before the single my-triage POST lives under `not_started` — and one screen serves a whole
 * band of steps.
 *
 * Returns `null` when the wizard has nothing to drive: no status yet, or a practice we switched OFF
 * on purpose. A disabled site has already been through setup and turning it back on is a
 * conversation, not a wizard, so the tab keeps rendering its existing neutral "turned off" card
 * rather than the wizard growing a second copy of that copy.
 *
 * ORDER OF THE BRANCHES IS THE WHOLE CORRECTNESS ARGUMENT, and it is the same argument
 * `deriveTabState` makes: `enabled` is authoritative, a deliberate switch-off outranks any step,
 * and real progress outranks the country gate — a site that reached `waba_connected` must never be
 * shown "not available in your country" because of a stale gate result.
 */
export function deriveWizardStep(status) {
  if (!status) return null;
  // `enabled` is the authoritative flag on the backend and stays authoritative here: if the channel
  // is actually ON, a leftover disabled marker must never hide the live screen.
  if (status.enabled) return 'live';
  // The wizard does not drive a switched-off site. Null hands the tab back its own card. Checked
  // BEFORE the step branches for the same reason deriveTabState checks it there: a site switched
  // off on purpose has usually made real progress — often all the way to step 'live' — so a
  // step-first ordering would render the finished screen for a practice whose WhatsApp is off.
  if (status.disabled) return null;
  if (status.step === 'live') return 'live';

  // Progress first: the back half is unattended and its band is derived positionally, so a step
  // added inside it is picked up here without an edit.
  if (IN_PROGRESS_STEPS.includes(status.step)) return 'progress';
  // The handoff terminus — a person has it, and my-handoff is the only way in.
  if (status.step === 'number_pending') return 'handoff_pending';
  // link_sent rides with number_ready for the same reason deriveTabState maps it back to
  // not_started: my-connect-init advances to link_sent on every click, so an abandoned Facebook
  // popup must leave the practice on a screen with a working Connect button.
  if (status.step === 'number_ready' || status.step === 'link_sent') return 'connect';
  // Triage is committed; which screen comes next is decided by the answers themselves.
  if (status.step === 'triage') return isUsPurchasePath(status.triage) ? 'number' : 'handoff';
  // Checked LAST among the real states: only a site still at not_started can be hard-blocked
  // (my-triage refuses to advance one), so this can never hide progress.
  if (status.countryGate && status.countryGate.hardBlocked) return 'blocked';
  // not_started — and any step this build does not recognize. Starting at the beginning is the
  // safe failure: the wizard's first action is a POST the server refuses with a clear message if
  // the site is in fact already under way, which is far better than claiming progress we cannot see.
  return 'intro';
}

/**
 * The screens a practice may walk BACK through, in order.
 *
 * ⚠️ THE RULED CONSTRAINT: my-triage accepts exactly one POST per site (it 409s
 * `onboarding_in_progress` for any step past `not_started`). So Back is a REVIEW of what was
 * submitted, never an edit-and-resubmit. Once `status.triage` is populated these screens render
 * read-only from the echoed server answers, and continuing forward from them fires no request at
 * all. See WhatsAppWizard.vue's `continueFromConfirm`.
 */
const REVIEW_PATH = ['intro', 'triage', 'confirm'];

/**
 * The bring-your-own path inserts the acknowledgement screen between the questions and the name, so
 * the practice reads and ticks every consequence of moving its existing number BEFORE the one
 * my-triage POST that stores its answers (the acks ride along in that same POST). Off this path there
 * is no existing number to move and nothing to acknowledge, so the screen never appears and the
 * review path is exactly what it was.
 */
const REVIEW_PATH_BYO = ['intro', 'triage', 'byo_ack', 'confirm'];

/**
 * The ordered pre-submit screens for THIS practice's answers — the BYO variant only when the answers
 * put it on the bring-your-own path. Passing the answers (the shell's live draft, or the echoed
 * committed triage) is what lets the one insertion be conditional without a second navigation path.
 * Omitting them yields the plain path, which is why the single-argument call sites stay correct.
 */
function reviewPathFor(answers) {
  return requiresByoAck(answers) ? REVIEW_PATH_BYO : REVIEW_PATH;
}

/**
 * The screen Back goes to, or null when Back should not be offered.
 *
 * The two post-triage branch screens both go back to `confirm`: the practice's answers are the last
 * thing they saw before the branch was decided, and that is what they came back to check.
 * Everything from `connect` onward has no Back — the number exists (or a person has the case) and
 * there is nothing behind it to change. `answers` selects the review path, so a BYO practice walks
 * back through `byo_ack` and a non-BYO one skips it, in both directions.
 */
export function previousWizardStep(current, answers) {
  if (current === 'number' || current === 'handoff') return 'confirm';
  const path = reviewPathFor(answers);
  const i = path.indexOf(current);
  return i > 0 ? path[i - 1] : null;
}

/**
 * The next screen while walking the review path forward, or null at its end.
 *
 * Null means "stop steering and render whatever the server says" — the wizard has no forward
 * opinion past `confirm`, because what comes next is decided by a POST and echoed back in status.
 * `answers` selects the review path so the acknowledgement screen is stepped through on the BYO path
 * and skipped otherwise.
 */
export function nextReviewStep(current, answers) {
  const path = reviewPathFor(answers);
  const i = path.indexOf(current);
  return (i > -1 && i < path.length - 1) ? path[i + 1] : null;
}

/**
 * The four coarse phases shown in the wizard's rail, so a practice can see how much is left.
 *
 * Deliberately COARSER than the screens: the rail answers "how far through am I", and a practice
 * does not need to know that answering questions and confirming a name are two screens. Branch
 * screens (`number` / `handoff`) share the "Your number" phase because that is what both are.
 */
const WIZARD_RAIL = [
  { key: 'start',   label: 'Get started', steps: ['intro'] },
  { key: 'number',  label: 'Your number', steps: ['triage', 'byo_ack', 'confirm', 'number', 'handoff'] },
  { key: 'connect', label: 'Connect',     steps: ['connect'] },
  { key: 'setup',   label: 'Setup',       steps: ['progress', 'live'] },
];

/**
 * [{ key, label, state: done|current|pending }] for the wizard rail.
 *
 * Returns [] for any screen off the happy path — `handoff_pending` and `blocked` are ENDINGS, and
 * a progress rail above them would suggest there is more of this flow to come when there is not.
 */
export function wizardRailStates(stepKey) {
  if (stepKey === 'live') return WIZARD_RAIL.map((g) => ({ key: g.key, label: g.label, state: 'done' }));
  const at = WIZARD_RAIL.findIndex((g) => g.steps.includes(stepKey));
  if (at === -1) return [];
  return WIZARD_RAIL.map((g, i) => ({
    key: g.key,
    label: g.label,
    state: i < at ? 'done' : (i === at ? 'current' : 'pending'),
  }));
}
